"use client";

import { FC, useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { Howl } from "howler";

import { ElProps, Nullable } from "@Shared/types";
import { twcx } from "@Shared/utils";

const MUSIC_SRC = "/audio/bg.mp3";
const MUSIC_VOLUME = 0.75;
const MUSIC_FADE_DURATION = 750;

const LINES_DELAY = gsap.utils.random(0, 0.5, 0.1, true);
const LINES_DURATION = gsap.utils.random(0.4, 0.5, 0.1, true);

export type MusicTogglerProps = ElProps<"button">;

export const MusicToggler: FC<MusicTogglerProps> = ({ className, ...restProps }) => {
    const refComponent = useRef<HTMLButtonElement>(null);
    const refMusic = useRef<Nullable<Howl>>(null);
    const refMusicTimeout = useRef<ReturnType<typeof setTimeout>>();

    const [isPlaying, setIsPlaying] = useState(false);

    const isMusicLoaded = () => refMusic.current?.state() === "loaded";

    const handleClick = () => {
        if (isMusicLoaded()) {
            setIsPlaying(prev => !prev);
        }
    };

    useEffect(() => {
        if (refMusic.current) {
            return;
        }

        refMusic.current = new Howl({
            src: [MUSIC_SRC],
            loop: true,
            volume: MUSIC_VOLUME,
        });

        return () => {
            refMusic.current?.unload();

            refMusic.current = null;
            refMusicTimeout.current = undefined;
        };
    }, []);

    useEffect(() => {
        if (!refComponent.current || !isMusicLoaded()) {
            return;
        }

        let tween: gsap.core.Tween;
        let tl: gsap.core.Timeline;

        if (isPlaying) {
            tl = gsap.timeline();

            [...refComponent.current.children].forEach(child => {
                tl.to(
                    child,
                    {
                        delay: LINES_DELAY(),
                        duration: LINES_DURATION(),
                        repeat: -1,
                        repeatRefresh: true,
                        yoyo: true,
                        scaleY: "random(0.3, 0.8)",
                    },
                    0
                );
            });

            refMusic.current?.play();
            refMusic.current?.fade(0, MUSIC_VOLUME, MUSIC_FADE_DURATION);
        } else {
            tween = gsap.to(refComponent.current.children, {
                scaleY: 1,
            });

            refMusic.current?.fade(MUSIC_VOLUME, 0, MUSIC_FADE_DURATION);
        }

        refMusicTimeout.current = setTimeout(() => {
            if (!isPlaying && refMusic.current?.volume() === 0) {
                refMusic.current.pause();
            }
        }, MUSIC_FADE_DURATION);

        return () => {
            clearTimeout(refMusicTimeout.current);

            tween?.kill();
            tl?.kill();
        };
    }, [isPlaying]);

    return (
        <button
            ref={refComponent}
            className={twcx(
                "relative z-0 flex size-9 shrink-0 items-center justify-center gap-0.5 rounded-full border border-black text-text-gray",
                "lg:size-10 lg:transition-colors lg:hover:bg-black lg:hover:text-white",
                {
                    "text-accent-2": isPlaying,
                },
                className
            )}
            type="button"
            aria-label="Toggle music"
            onClick={handleClick}
            {...restProps}
        >
            {new Array(5).fill(null).map((_, index) => (
                <div key={index} className="h-4 w-0.5 shrink-0 origin-bottom rounded-md bg-current" />
            ))}
        </button>
    );
};
